import React, { useContext, useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import { Box, Button, Chip, Paper, Stack, Table, TableBody, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Carousel from 'react-material-ui-carousel';
import SelectMUI from 'components/mui/select/select.component';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { ArrowBackIcon, ArrowForwardIcon } from 'components/mui';
import CinchNotes from 'components/library/notes/cinch-notes.component';
import { CustomerDrawerContext } from 'context/customer-drawer-context';
import { history } from '../../../root.component';
import { DueChip } from '../../../pages/task-manager/due-chip.component';
import { SOURCE_TYPE_LABELS, getStageColor } from '../../../pages/task-manager/task-manager.constants';
import { TASK_STATUS_ORDER, TaskStatusIcon, TaskStatusPill, describeTaskStatus, getTaskStatus, sortTasksForDetails } from './task-status';
function renderNullable(value) {
  return value !== null && value !== undefined && value !== '' ? value : <Typography variant="nullData" color="#BEBEBE">
      <span>--</span>
    </Typography>;
}
const formatWhen = when => when ? moment(when).format('MMM D, YYYY') : null;
const Row = ({
  label,
  children
}) => <TableRow>
    <TableCell sx={{
    width: '150px',
    verticalAlign: 'top'
  }}>
      <Typography variant="tableHeader">{label}</Typography>
    </TableCell>
    <TableCell>{children}</TableCell>
  </TableRow>;

/**
 * CIN-7048: the Details tab's Tasks view. One read-only card per task linked to
 * this customer (or a customer merged into them), colored by status so the state
 * reads at a glance. Editing the task is deliberately out of scope: "Open in Task
 * Manager" hands the task to the Task Manager, which owns every change. Notes are
 * the one write allowed here (plan DC4), because noting a call-back is exactly what
 * a rep does from the customer record.
 *
 * `tasks` is the `tasks` key of GET /customers/{id}/actions?type=task, the same
 * payload the Timeline uses, so both views agree on each task's status.
 */
export function TasksComponent(props) {
  const {
    tasks,
    selectedTask,
    _detailsTabs,
    handleDetailsTab,
    selectedDetailsTab,
    isLoading
  } = props;
  const {
    closeCustomerDrawer
  } = useContext(CustomerDrawerContext);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const ordered = useMemo(() => sortTasksForDetails(tasks), [tasks]);
  useEffect(() => {
    // Jump to a requested task (a new task, "Go to Task" from the Timeline, or a
    // task_id deep link); otherwise start at the most urgent card.
    let toSelect = 0;
    if (selectedTask && ordered.length) {
      toSelect = ordered.findIndex(task => task.id === selectedTask);
      // Not in the list (deleted, outside the viewer's Catalyst scope, or not on
      // the read replica yet): start at the most urgent card instead.
      if (toSelect === -1) toSelect = 0;
    }
    setSelectedIndex(toSelect);
  }, [selectedTask, ordered]);
  const counts = useMemo(() => TASK_STATUS_ORDER.map(status => ({
    status,
    count: ordered.filter(task => task.status === status).length
  })).filter(entry => entry.count > 0), [ordered]);

  // Lands on the Task Manager filtered to just this task (?id=, shown as a chip the
  // user can clear) on the task's own board, the way a review link lands on the
  // Reviews list filtered to that one review. The state marker lets the Task Manager
  // apply it even when the drawer was opened on top of it.
  const openInTaskManager = task => {
    closeCustomerDrawer();
    const params = new URLSearchParams({
      id: task.id
    });
    if (task.board) params.set('board', task.board);
    history.push({
      pathname: '/task-manager',
      search: `?${params.toString()}`,
      state: {
        fromCustomerDrawer: true
      }
    });
  };
  return <Stack sx={{
    'height': '100%',
    'background': '#E4E9F1',
    'borderBottomLeftRadius': '28px',
    'borderBottomRightRadius': '28px',
    'overflowY': 'scroll',
    'scrollbarWidth': 'thin',
    '&::-webkit-scrollbar': {
      width: '0.4em'
    },
    '&::-webkit-scrollbar-track': {
      background: '#f1f1f1',
      marginTop: '25px',
      marginBottom: '25px'
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#888'
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#555'
    }
  }} className="d-flex h-100" spacing="14px">
      <Box className="d-flex w-100 justify-content-between" sx={{
      position: 'relative',
      pt: '14px',
      pb: '38px',
      display: 'flex'
    }}>
        <span />
        <Box sx={{
        position: 'absolute',
        left: '37.5%'
      }}>
          <SelectMUI height="36px" width="180px" top="6px" zIndex={9001} options={_detailsTabs} value={selectedDetailsTab || ''} onChange={opt => handleDetailsTab(opt.key)} />
        </Box>
        {ordered.length > 0 && <Box sx={{
        position: 'absolute',
        left: '77%',
        top: '24px'
      }}>{`${selectedIndex + 1} of ${ordered.length} Items`}</Box>}
      </Box>

      <Stack>
        {isLoading ? <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        pt: '25px'
      }}>
            <CircularProgress />
          </Box> : ordered.length > 0 ? <>
            {/* CIN-7048 (plan D13): a quick-glance strip, one dot per task in card
                order and colored by status. Clicking a dot jumps to that card.
                The counts sit on their own line under the dots (user, 2026-09-28):
                beside them they crowded the strip, and they're the only legend
                for the colors. The dots wrap (useFlexGap keeps wrapped rows
                aligned), since a long-time customer can have dozens of tasks. */}
            <Stack spacing="6px" sx={{
          alignItems: 'center',
          pb: '12px'
        }}>
              <Stack direction="row" spacing="8px" useFlexGap data-testid="task-glance-dots" sx={{
            flexWrap: 'wrap',
            justifyContent: 'center',
            px: '16px'
          }}>
                {ordered.map((task, index) => <Tooltip key={task.id} title={`${task.name} · ${getTaskStatus(task.status).label}`}>
                    <Box component="button" type="button"
              // The status is in the label too, so it isn't told by color alone.
              aria-label={`Show ${task.name}, ${getTaskStatus(task.status).label}`} data-status={task.status} onClick={() => setSelectedIndex(index)} sx={{
                width: '12px',
                height: '12px',
                p: 0,
                border: 0,
                borderRadius: '50%',
                cursor: 'pointer',
                backgroundColor: getTaskStatus(task.status).color,
                boxShadow: index === selectedIndex ? '0 0 0 2px #fff, 0 0 0 4px #1D252D' : 'none'
              }} />
                  </Tooltip>)}
              </Stack>
              <Typography sx={{
            fontSize: '12px',
            textAlign: 'center'
          }} data-testid="task-glance-counts">
                {counts.map(entry => `${entry.count} ${getTaskStatus(entry.status).label.toLowerCase()}`).join(' · ')}
              </Typography>
            </Stack>

            <Carousel index={selectedIndex} navButtonsAlwaysVisible cycleNavigation={false} autoPlay={false} animation="slide" indicators={false} PrevIcon={<ArrowBackIcon fill="#fff" />} NextIcon={<ArrowForwardIcon fill="#fff" />} onChange={now => setSelectedIndex(now)}>
              {ordered.map(task => {
            const status = getTaskStatus(task.status);
            const stageColor = getStageColor(task.stage_color);
            return <Box key={task.id} sx={{
              px: '50px'
            }}>
                    <Paper data-testid="drawer-task-card" data-status={task.status} sx={{
                'borderRadius': '10px',
                // The card's left edge carries the status color (plan D13).
                'borderLeft': `5px solid ${status.color}`,
                'overflowY': 'scroll',
                'scrollbarWidth': 'thin',
                '&::-webkit-scrollbar': {
                  width: '0.4em'
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  marginTop: '10px',
                  marginBottom: '10px'
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#888'
                },
                'height': '64vh'
              }}>
                      <Stack direction="row" spacing="10px" sx={{
                  alignItems: 'center',
                  px: '16px',
                  py: '14px',
                  borderBottom: '1px solid #EDEFF1'
                }}>
                        <TaskStatusIcon status={task.status} filled />
                        <Typography sx={{
                    fontWeight: 600,
                    fontSize: '16px',
                    flex: 1,
                    minWidth: 0,
                    overflowWrap: 'anywhere'
                  }}>
                          {task.name}
                        </Typography>
                        <TaskStatusPill status={task.status} />
                      </Stack>

                      <Table>
                        <TableBody>
                          <Row label="Status">
                            <Typography sx={{
                        color: status.chip.text
                      }}>{describeTaskStatus(task)}</Typography>
                          </Row>
                          <Row label="Stage">
                            <Stack direction="row" spacing="6px" sx={{
                        alignItems: 'center',
                        flexWrap: 'wrap'
                      }}>
                              {task.board_name && <Typography>{task.board_name} ·</Typography>}
                              {/* The stage keeps its own configured color; the status
                                  pill above names the status, so a red stage isn't
                                  read as Overdue. */}
                              <Chip size="small" label={task.stage_name} sx={{
                          backgroundColor: stageColor.bg,
                          color: stageColor.text,
                          fontWeight: 600
                        }} />
                            </Stack>
                          </Row>
                          <Row label="Due date">{task.due_date ? <DueChip task={task} /> : renderNullable(null)}</Row>
                          <Row label="Assigned to">{renderNullable(task.assigned_to_name || 'Unassigned')}</Row>
                          <Row label="Location">{renderNullable(task.location_name)}</Row>
                          <Row label="Type">{renderNullable(SOURCE_TYPE_LABELS[task.source_type] || task.source_type)}</Row>
                          <Row label="Categories">
                            {task.categories?.length ? <Stack direction="row" spacing="6px" sx={{
                        flexWrap: 'wrap',
                        rowGap: '6px'
                      }}>
                                {task.categories.map(category => <Chip key={category} size="small" label={category} />)}
                              </Stack> : renderNullable(null)}
                          </Row>
                          <Row label="Created">
                            {renderNullable([formatWhen(task.created_when), task.created_by_name && `by ${task.created_by_name}`].filter(Boolean).join(' '))}
                          </Row>
                          {task.completed_when && <Row label="Completed">
                              {[formatWhen(task.completed_when), task.completed_by_name && `by ${task.completed_by_name}`].filter(Boolean).join(' ')}
                            </Row>}
                          <Row label="Description">
                            {task.description ? <Typography sx={{
                        whiteSpace: 'pre-wrap'
                      }}>{task.description}</Typography> : renderNullable(null)}
                          </Row>
                          {task.linked_records?.length > 0 && <Row label="Linked records">
                              <Stack direction="row" spacing="6px" sx={{
                        flexWrap: 'wrap',
                        rowGap: '6px'
                      }}>
                                {task.linked_records.map(record => <Chip key={record.type} size="small" variant="outlined" label={record.label} />)}
                              </Stack>
                            </Row>}
                        </TableBody>
                      </Table>

                      {/* The one write on this card: the task's own notes (plan DC4). */}
                      <Box sx={{
                  px: '16px',
                  pt: '8px'
                }}>
                        <CinchNotes objectId={task.id} type="task" title="Notes" />
                      </Box>

                      <Box sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  p: '16px'
                }}>
                        <Button variant="contained" startIcon={<OpenInNewIcon />} onClick={() => openInTaskManager(task)} data-testid="open-in-task-manager" sx={{
                    textTransform: 'none'
                  }}>
                          Open in Task Manager
                        </Button>
                      </Box>
                    </Paper>
                  </Box>;
          })}
            </Carousel>
          </> : <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        width: '100%'
      }}>
            {/* Not "Customer has no tasks": a Catalyst sees only tasks at their
                locations, and a user without access sees none at all. */}
            <Typography>No tasks to show</Typography>
          </Box>}
      </Stack>
    </Stack>;
}